import type { ReactNode } from "react";
import { Button } from "./Button";
import { buttonClass } from "./Button";

export interface EmptyStateProps {
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  actionVariant?: "primary" | "outline";
  icon?: ReactNode;
  className?: string;
  /**
   * "card" (default): a standalone SURFACE — for page-level empties. A
   * hairline border and no drop shadow, so it sits at the same elevation as
   * the list or panel beside it. It used to carry `--shadow-md`, which made
   * the pane that means "there is nothing here" the most raised thing on the
   * screen — an empty state announcing itself louder than the content it is
   * standing in for.
   * "plain": no chrome at all — for an empty state rendered INSIDE an existing
   * card, where any second box is a design-doc violation ("no nested cards").
   */
  variant?: "card" | "plain";
}

export function EmptyState({
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  actionVariant = "primary",
  icon,
  className,
  variant = "card",
}: EmptyStateProps) {
  return (
    <div
      data-ui="empty"
      className={`${
        variant === "card"
          ? "rounded-[var(--radius-card,16px)] border-[length:var(--border-card-width,1px)] border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-8"
          : "px-2 py-6"
      } text-center ${className ?? ""}`}
    >
      {icon ? (
        <div data-ui="empty-glyph" className="mb-3 flex justify-center">
          {icon}
        </div>
      ) : null}
      <p
        data-ui="empty-title"
        className="text-sm font-semibold text-[color:var(--text-secondary)]"
      >
        {title}
      </p>
      <p
        data-ui="empty-body"
        className="mt-2 text-[13px] text-[color:var(--text-muted)]"
      >
        {description}
      </p>
      {actionLabel ? (
        <div data-ui="empty-action" className="mt-4 flex justify-center">
          {actionHref ? (
            <a
              href={actionHref}
              data-ui="button"
              data-variant={actionVariant}
              data-size="md"
              className={buttonClass({
                variant: actionVariant,
                size: "md",
              })}
            >
              {actionLabel}
            </a>
          ) : onAction ? (
            <Button
              type="button"
              onClick={onAction}
              variant={actionVariant}
              size="md"
            >
              {actionLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
